import React from 'react'
import { useSelector } from 'react-redux'
import * as R from 'ramda'

import { fetchTemplate } from 'api/templates'
import { getLocation } from 'ducks/router'
import { useConfirmationModal, useFetch } from 'hooks'
import { jobs } from 'orm'
import { useSendTestsMessageForm } from './common/SendTestsMessageForm/hooks'

const useTestsReportModalQueries = () => {
  const handleFetchMessageTemplates = React.useCallback(
    async ({ project }) => await fetchTemplate({ project }),
    [],
  )

  return {
    handleFetchMessageTemplates,
  }
}

const useSendTestsMessageModalData = state => {
  const { isOpen } = state

  const { handleFetchMessageTemplates } = useTestsReportModalQueries()

  const {
    payload: { id },
  } = useSelector(getLocation)
  const job = useSelector(state => jobs.getItem()(state, { pk: id }))

  const handleFetch = React.useCallback(async () => {
    if (R.or(!id, !isOpen)) return

    return await handleFetchMessageTemplates({ project: id })
  }, [id, handleFetchMessageTemplates, isOpen])

  const {
    state: { response, isFetching },
  } = useFetch(handleFetch)

  return {
    state: {
      company: job?.company_name,
      templates: response?.results,
      isFetching,
    },
  }
}

const useSendTestsMessageGroupModalSteps = state => {
  const { fields, isOpen } = state

  const [activeStep, setActiveStep] = React.useState(fields[0]?.key)

  const steps = React.useMemo(() => R.map(({ key }) => ({ key }), fields), [
    fields,
  ])

  const currentStepIndex = React.useMemo(
    () => R.findIndex(R.propEq('key', activeStep))(steps),
    [activeStep, steps],
  )

  const handleChangeStep = React.useCallback(
    step => {
      setActiveStep(step)
    },
    [setActiveStep],
  )

  const handleNextStep = React.useCallback(() => {
    if (R.gte(currentStepIndex, R.length(steps) - 1)) return

    const nextStep = steps[currentStepIndex + 1]?.key

    handleChangeStep(nextStep)
  }, [currentStepIndex, steps, handleChangeStep])

  const handlePreviousStep = React.useCallback(() => {
    if (R.lte(currentStepIndex, 0)) return

    const previousStep = steps[currentStepIndex - 1]?.key

    handleChangeStep(previousStep)
  }, [currentStepIndex, steps, handleChangeStep])

  const isFirstStep = R.equals(activeStep, R.head(steps)?.key)
  const isLastStep = R.equals(activeStep, R.last(steps)?.key)

  React.useEffect(() => {
    if (isOpen) {
      setActiveStep(fields[0]?.key)
    }
  }, [fields, isOpen])

  return {
    state: { activeStep, currentStepIndex, steps, isFirstStep, isLastStep },
    handlers: { handleNextStep, handlePreviousStep },
  }
}

export const useSendTestsMessageModalState = (state, handlers) => {
  const { candidates, name, deadline, isOpen } = state
  const { onCancel, onClose, onConfirm } = handlers

  const {
    state: { company, templates },
  } = useSendTestsMessageModalData({ isOpen })

  const {
    state: { control, fields, formState, isDirtyOrInvalid },
    handlers: { handleFormReset, handleFormSubmit },
  } = useSendTestsMessageForm(
    { candidates, company, deadline, name, templates, isOpen },
    { onConfirm },
  )

  const {
    state: { activeStep, currentStepIndex, steps, isFirstStep, isLastStep },
    handlers: { handleNextStep, handlePreviousStep },
  } = useSendTestsMessageGroupModalSteps({ fields, isOpen })

  const {
    handleConfirmationClose,
    handleConfirmationConfirm,
    handleConfirmationOpen,
    isConfirmationOpen,
  } = useConfirmationModal(() => {
    handleFormReset()
    onClose()
  })

  const handleCancel = React.useCallback(() => {
    isDirtyOrInvalid ? handleConfirmationOpen() : onCancel()
  }, [isDirtyOrInvalid, handleConfirmationOpen, onCancel])

  const handleClose = React.useCallback(() => {
    isDirtyOrInvalid ? handleConfirmationOpen() : onClose()
  }, [isDirtyOrInvalid, handleConfirmationOpen, onClose])

  const handleConfirm = React.useCallback(handleFormSubmit, [handleFormSubmit])

  return {
    state: {
      activeStep,
      currentStepIndex,
      form: { control, formState },
      steps,
      isConfirmationOpen,
      isFirstStep,
      isLastStep,
    },
    handlers: {
      handleCancel,
      handleClose,
      handleConfirm,
      handleConfirmationClose,
      handleConfirmationConfirm,
      handleNextStep,
      handlePreviousStep,
    },
  }
}
